import { LogLevel } from '../constants/log.constants';

import { Logger } from './logger';

export class ServiceWorkerActivation {

  private logger: Logger;

  constructor(
    logLevel: LogLevel) {

    this.logger = new Logger(this.constructor.name, logLevel);
  }

  // Returns a Promise that resolves with the active service worker after it has become 'activated'.
  async wait(): Promise<ServiceWorker> {

    const logPrefix = `wait`;
    this.logger.log(`${logPrefix}: enter`);

    let promise: Promise<ServiceWorker>;

    // 1. Check if the service worker is already active
    if (navigator.serviceWorker.controller) {
      this.logger.log(`${logPrefix}: service worker is already active => resolving`);
      promise = Promise.resolve(navigator.serviceWorker.controller);
    }
    else {
      // 2. Wait for the service worker registration to be complete
      this.logger.warn(`${logPrefix}: service worker is NOT active yet: waiting for the service worker registration to be complete...`);
      const registration = await navigator.serviceWorker.ready;

      // 3. The service worker is registered, but might not be 'active' yet.
      // Check the state of the installing worker or the existing active worker.
      this.logger.warn(`${logPrefix}: service worker is now registered, but might not be 'active' yet.`);
      if (registration.active) {
        // Already active (e.g., on subsequent loads)
        this.logger.warn(`${logPrefix}: service worker is already active (e.g.: on subsequent loads) => resolving`);
        promise = Promise.resolve(registration.active);
      }
      else {
        // 4. If an installing worker exists, wait for it to change to 'activated'.
        this.logger.warn(`${logPrefix}: if an installing service worker exists, wait for it to change to 'activated'.`);
        promise = new Promise<ServiceWorker>((resolve) => {
          // If no 'installing' worker, we might have caught it between states.
          // This is a common pattern to ensure the final active state is reached.
          const worker = registration.installing;
          if (worker) {
            this.logger.warn(`${logPrefix}: waiting for installing service worker change to 'activated'...`);
            const handler = () => {
              this.logger.warn(`${logPrefix}: service worker has changed status to '${worker.state}'.`);
              if (worker.state === 'activated') {
                this.logger.warn(`${logPrefix}: service worker has changed status to '${worker.state}' => resolving`);
                worker.removeEventListener('statechange', handler);
                resolve(worker);
              }
              else {
                this.logger.warn(`${logPrefix}: service worker has changed status to '${worker.state}' => still waiting`);
              }
            };
            worker.addEventListener('statechange', handler);
          }
          else {
            this.logger.warn(`${logPrefix}: no active service worker registration and no 'installing' service worker present => resolving`);
            // Fallback for cases where 'registration.active' was null but no 'installing' worker is present.
            // This relies on the 'active' worker eventually being set and becoming the controller.
            // A more robust check might involve re-checking 'registration.active' after a short delay, 
            // but for a typical setup, waiting for 'navigator.serviceWorker.ready' is usually sufficient
            // to get the 'registration' object, and the statechange listener is for the *initial* activation.
            // Since we already resolved the Promise above if 'registration.active' existed, we resolve 
            // with the new 'registration.active' for safety.
            resolve(registration.active);
          }
        });
      }
    }

    this.logger.log(`${logPrefix}: leave`);
    return promise;
  }
}
